import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  Avatar,
  Divider,
  Grid,
  Paper,
  IconButton
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  CalendarToday as CalendarIcon,
  AccessTime as TimeIcon,
  LocationOn as LocationIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  Phone as PhoneIcon,
  CreditCard as CreditCardIcon,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import BottomNavigation from '../components/BottomNavigation';
import useResponsive from '../hooks/useResponsive';
import { formatCurrency } from '../utils/countryUtils';
import { formatDateLong, formatDateShort } from '../utils/timezoneUtils';

const BookingSuccess = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { 
    isMobile, 
    isTablet, 
    isDesktop,
    getResponsiveValue,
    getResponsiveSpacing,
    getDialogProps 
  } = useResponsive();
  const [bookingData, setBookingData] = useState(null);
  const [providerData, setProviderData] = useState(null);

  useEffect(() => {
    console.log('🔍 [BOOKING SUCCESS] useEffect triggered');
    console.log('🔍 [BOOKING SUCCESS] location:', location);
    console.log('🔍 [BOOKING SUCCESS] location.state:', location.state);
    console.log('🔍 [BOOKING SUCCESS] location.state?.bookingData:', location.state?.bookingData);
    console.log('🔍 [BOOKING SUCCESS] location.state?.providerData:', location.state?.providerData);
    
    // Get booking data from location state
    if (location.state?.bookingData) {
      console.log('🔍 [BOOKING SUCCESS] Received booking data:', location.state.bookingData);
      console.log('🔍 [BOOKING SUCCESS] Booking number:', location.state.bookingData.bookingNumber);
      console.log('🔍 [BOOKING SUCCESS] Payment confirmation:', location.state.bookingData.paymentConfirmationNumber);
      console.log('🔍 [BOOKING SUCCESS] Stripe payment intent:', location.state.bookingData.stripePaymentIntentId);
      console.log('🔍 [BOOKING SUCCESS] Provider name:', location.state.bookingData.providerName);
      console.log('🔍 [BOOKING SUCCESS] Provider type:', location.state.bookingData.providerType);
      console.log('🔍 [BOOKING SUCCESS] Provider phone:', location.state.bookingData.providerPhone);
      console.log('🔍 [BOOKING SUCCESS] Provider address:', location.state.bookingData.providerAddress);
      
      setBookingData(location.state.bookingData);
      setProviderData(location.state.providerData);
    } else {
      console.log('🔍 [BOOKING SUCCESS] No booking data found, staying on page');
      console.log('🔍 [BOOKING SUCCESS] location.state exists:', !!location.state);
      console.log('🔍 [BOOKING SUCCESS] location.state.bookingData exists:', !!location.state?.bookingData);
      // If no data in state, stay on page and show message
      setBookingData(null);
    }
  }, [location.state, navigate]);

  const handleBackToHome = () => {
    navigate('/home');
  };

  // Copy logs function removed

  // Function to regenerate booking success page with real data from backend
  const regenerateWithRealData = async () => {
    try {
      console.log('🔄 [BOOKING SUCCESS] Fetching real booking data from backend...');
      
      // Fetch the latest booking from backend
      const response = await fetch('/api/customer/bookings/latest', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const bookingResult = await response.json();
      console.log('🔄 [BOOKING SUCCESS] Backend booking data received:', bookingResult);

      if (bookingResult.success && bookingResult.booking) {
        const backendBooking = bookingResult.booking;
        
        // Transform backend data to match our frontend format
        const realBookingData = {
          bookingNumber: backendBooking.bookingNumber,
          paymentConfirmationNumber: backendBooking.paymentConfirmationId,
          stripePaymentIntentId: backendBooking.paymentConfirmationId,
          providerName: backendBooking.providerName || 'Provider',
          providerType: backendBooking.providerType || 'Salon',
          providerPhone: backendBooking.providerPhone || '',
          providerAddress: backendBooking.providerAddress || '',
          serviceAddress: backendBooking.serviceAddress || '',
          date: backendBooking.date ? new Date(backendBooking.date).toISOString().split('T')[0] : '',
          startTime: backendBooking.startTime || '',
          duration: backendBooking.duration || 30,
          services: backendBooking.items || [],
          teamMember: {
            id: backendBooking.teamMemberId?._id || backendBooking.teamMemberId || '',
            name: backendBooking.teamMemberId?.name || backendBooking.teamMemberName || ''
          },
          totalAmount: backendBooking.pricing?.totalPaid || 0,
          subTotal: backendBooking.pricing?.subtotal || 0,
          paymentMethod: backendBooking.paymentMethod || 'Credit Card',
          serviceType: backendBooking.mode || 'in_salon',
          tipAmount: backendBooking.tipAmount || 0,
          appliedWalletBalance: backendBooking.appliedWalletBalance || 0,
          appliedGems: backendBooking.appliedGems || 0,
          specialInstructions: backendBooking.specialInstructions || ''
        };

        const realProviderData = {
          id: backendBooking.providerId?._id || backendBooking.providerId || '',
          name: backendBooking.providerId?.businessName || backendBooking.providerName || 'Provider',
          providerType: backendBooking.providerId?.category || backendBooking.providerType || 'salon',
          phone: backendBooking.providerId?.phone || backendBooking.providerPhone || '',
          address: backendBooking.providerId?.address || backendBooking.providerAddress || ''
        };

        // Update state with real data
        setBookingData(realBookingData);
        setProviderData(realProviderData);
        
        console.log('🔄 [BOOKING SUCCESS] Regenerated with real data:', { realBookingData, realProviderData });
        alert('✅ Booking success page regenerated with real data from backend!\n\nAll data is now fetched from the actual booking record.');
      } else {
        throw new Error('No booking data found in response');
      }
    } catch (error) {
      console.error('❌ [BOOKING SUCCESS] Failed to fetch real booking data:', error);
      alert(`❌ Failed to fetch real booking data: ${error.message}\n\nFalling back to test data.`);
      
      // Fallback to test data if backend fetch fails
      const testBookingData = {
        bookingNumber: 'BK-39282413-4AQ5',
        paymentConfirmationNumber: 'pi_3S55QLK5LRvvauRY0k3OPNQc',
        stripePaymentIntentId: 'pi_3S55QLK5LRvvauRY0k3OPNQc',
        providerName: "Sarah's Beauty Salon",
        providerType: 'Salon',
        providerPhone: '+61412345678',
        providerAddress: '123 Main Street, Sydney NSW 2000',
        serviceAddress: null,
        date: '2025-09-14',
        startTime: '14:30',
        duration: 30,
        services: [
          {
            serviceId: '68a7edf6e167a41a182db65c',
            serviceName: 'O3 facial',
            quantity: 1,
            price: 50,
            duration: 30,
            color: '#f44336'
          }
        ],
        teamMember: {
          id: '68a6a9f9ba9c1a89bea37665',
          name: 'Dinisha shah'
        },
        totalAmount: 50,
        subTotal: 50,
        paymentMethod: 'Credit Card',
        serviceType: 'in_salon'
      };

      const testProviderData = {
        id: '68a6a8549f9c5ac5c5a5f9e4',
        name: "Sarah's Beauty Salon",
        providerType: 'salon',
        phone: '+61412345678',
        address: '123 Main Street, Sydney NSW 2000'
      };

      setBookingData(testBookingData);
      setProviderData(testProviderData);
    }
  };


  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return formatDateLong(dateString);
  };

  const formatTime = (timeString) => {
    if (!timeString) return 'N/A';
    return timeString;
  };

  const getServiceColor = (color) => {
    return color || '#1976d2';
  };

  if (!bookingData) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh',
        p: 3
      }}>
        <Typography variant="h5" sx={{ mb: 2, textAlign: 'center' }}>
          No Booking Data Available
        </Typography>
        <Typography variant="body1" sx={{ mb: 3, textAlign: 'center', color: 'text.secondary' }}>
          Please use the "Regenerate with Real Data" button to fetch your latest booking, or go back to home.
        </Typography>
        <Button 
          variant="contained" 
          onClick={handleBackToHome}
          startIcon={<ArrowBackIcon />}
        >
          Back to Home
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      minHeight: '100vh',
      backgroundColor: '#f8fafc',
      pb: 8 // Add padding bottom for bottom navigation
    }}>
      {/* Header - Same Dark Blue as Home Page */}
      <Box sx={{ 
        backgroundColor: '#1a237e',
        color: 'white',
        p: 2,
      display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <IconButton 
          onClick={handleBackToHome}
          sx={{ color: 'white' }}
        >
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
          Booking Confirmed
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <IconButton
            onClick={regenerateWithRealData}
            sx={{ color: 'white' }}
            title="Regenerate with real data from backend"
          >
            🔄
          </IconButton>
        </Box>
      </Box>

      {/* Main Content */}
      <Box sx={{ p: 2, maxWidth: 800, mx: 'auto' }}>
        {/* Booking Number */}
        <Box sx={{ 
          textAlign: 'center', 
          mb: 3, 
          p: 2, 
          backgroundColor: '#f0fdf4', 
          borderRadius: 2,
          border: '2px solid #10b981'
        }}>
          <CheckCircleIcon sx={{ fontSize: 48, color: '#10b981', mb: 1 }} />
          <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#10b981', mb: 1 }}>
            Booking Confirmed!
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#374151' }}>
            Booking Number: {bookingData.bookingNumber || 'N/A'}
          </Typography>
        </Box>

        {/* Booking Details Card */}
        <Card sx={{ mb: 3, boxShadow: 3 }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
              <CalendarIcon color="primary" />
                Booking Details
          </Typography>
          
            <Grid container spacing={3}>
              {/* Date & Time */}
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2, backgroundColor: '#f8fafc' }}>
                  <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CalendarIcon color="primary" />
                    Date & Time
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    <strong>Date:</strong> {formatDate(bookingData.date)}
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    <strong>Time:</strong> {formatTime(bookingData.startTime)}
                  </Typography>
                  <Typography variant="body1">
                    <strong>Duration:</strong> {bookingData.duration || 'N/A'} minutes
                  </Typography>
                </Paper>
              </Grid>

              {/* Booking Type */}
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2, backgroundColor: '#f8fafc' }}>
                  <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LocationIcon color="primary" />
                    Service Type
                  </Typography>
                  <Chip 
                    label={bookingData.serviceType === 'at_home' ? 'At Home Service' : 'In Salon Service'}
                    color={bookingData.serviceType === 'at_home' ? 'secondary' : 'primary'}
                    sx={{ mb: 1 }}
                  />
                  {bookingData.serviceAddress && (
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      <strong>Address:</strong> {bookingData.serviceAddress}
                    </Typography>
                  )}
                </Paper>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Reschedule Section - Only show if this is a reschedule */}
        {bookingData.isReschedule && bookingData.originalBooking && (
          <Card sx={{ mb: 3, boxShadow: 3, border: '2px solid #f59e0b' }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 3, display: 'flex', alignItems: 'center', gap: 1, color: '#f59e0b' }}>
                🔄 Reschedule Details
          </Typography>

              <Grid container spacing={3}>
                {/* Old Appointment */}
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 2, backgroundColor: '#fef3c7', border: '1px solid #f59e0b' }}>
                    <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1, color: '#92400e' }}>
                      📅 Original Appointment
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                      <strong>Date:</strong> {formatDate(bookingData.originalBooking.date)}
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                      <strong>Time:</strong> {formatTime(bookingData.originalBooking.startTime)}
                    </Typography>
                    <Typography variant="body1">
                      <strong>Duration:</strong> {bookingData.originalBooking.duration || 'N/A'} minutes
                    </Typography>
                  </Paper>
                </Grid>

                {/* New Appointment */}
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 2, backgroundColor: '#d1fae5', border: '1px solid #10b981' }}>
                    <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1, color: '#065f46' }}>
                      ✅ New Appointment
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                      <strong>Date:</strong> {formatDate(bookingData.date)}
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                      <strong>Time:</strong> {formatTime(bookingData.startTime)}
                    </Typography>
                    <Typography variant="body1">
                      <strong>Duration:</strong> {bookingData.duration || 'N/A'} minutes
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>

              {/* Reschedule Payment Details */}
              {bookingData.rescheduleFee > 0 && (
                <Box sx={{ mt: 3, p: 2, backgroundColor: '#f3f4f6', borderRadius: 2 }}>
                  <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    💳 Reschedule Payment
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="body2">
                        <strong>Payment Amount:</strong> {formatCurrency(bookingData.rescheduleFee || 0)}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2">
                        <strong>Payment Date:</strong> {formatDateShort(new Date())}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2">
                        <strong>Payment Method:</strong> {bookingData.paymentMethod || 'Card'}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2">
                        <strong>Confirmation:</strong> {bookingData.paymentConfirmationNumber || 'N/A'}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
              )}

              {/* Free Reschedule Notice */}
              {bookingData.rescheduleFee === 0 && (
                <Box sx={{ mt: 3, p: 2, backgroundColor: '#d1fae5', borderRadius: 2, border: '1px solid #10b981' }}>
                  <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#065f46' }}>
                    🎉 Free Reschedule - No additional charges applied
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        )}

        {/* Services Booked */}
        <Card sx={{ mb: 3, boxShadow: 3 }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 3 }}>
              Services Booked
            </Typography>
            {bookingData.services?.map((service, index) => (
              <Box key={index} sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                p: 2, 
                mb: 2, 
                backgroundColor: '#f8fafc',
                borderRadius: 2,
                border: `2px solid ${getServiceColor(service.color)}20`
              }}>
                <Box sx={{ 
                  width: 12, 
                  height: 12, 
                  borderRadius: '50%', 
                  backgroundColor: getServiceColor(service.color),
                  mr: 2
                }} />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    {service.serviceName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Duration: {service.duration} minutes • Price: {formatCurrency(service.price || 0)}
          </Typography>
                </Box>
              </Box>
            ))}
          </CardContent>
        </Card>

        {/* Provider Details */}
        <Card sx={{ mb: 3, boxShadow: 3 }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
              <BusinessIcon color="primary" />
              Provider Details
              </Typography>
              
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Avatar sx={{ mr: 2, bgcolor: 'primary.main' }}>
                <BusinessIcon />
              </Avatar>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                  {bookingData.providerName || 'Provider Name'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {(bookingData.providerType === 'salon' || bookingData.providerType === 'Salon') ? 'Salon' : 'Pros'}
                </Typography>
              </Box>
            </Box>

            {/* Provider Phone */}
            {bookingData.providerPhone && (
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <PhoneIcon sx={{ mr: 1, color: 'text.secondary' }} />
                <Typography variant="body2">
                  <strong>Phone:</strong> {bookingData.providerPhone}
                </Typography>
              </Box>
            )}

            {/* Provider Address (for Salon) or Service Address (for Pros) */}
            {(bookingData.providerType === 'salon' || bookingData.providerType === 'Salon') && bookingData.providerAddress && (
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <LocationIcon sx={{ mr: 1, color: 'text.secondary' }} />
                <Typography variant="body2">
                  <strong>Address:</strong> {bookingData.providerAddress}
                </Typography>
              </Box>
            )}

            {(bookingData.providerType === 'pros' || bookingData.providerType === 'Pros') && bookingData.serviceAddress && (
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <LocationIcon sx={{ mr: 1, color: 'text.secondary' }} />
                <Typography variant="body2">
                  <strong>Service Address:</strong> {bookingData.serviceAddress}
                </Typography>
              </Box>
            )}
            </CardContent>
          </Card>

        {/* Payment Details */}
        <Card sx={{ mb: 3, boxShadow: 3 }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
              <CreditCardIcon color="primary" />
              Payment Details
          </Typography>
          
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">Subtotal:</Typography>
                <Typography variant="h6">{formatCurrency(bookingData.subTotal || 0)}</Typography>
              </Grid>
              
              {/* Coupon Discount Line Item */}
              {(bookingData?.metadata?.couponDiscount > 0 || bookingData?.couponDiscount > 0) && (
                <Grid item xs={6}>
                  <Typography variant="body2" color="success.main">
                    🎉 Coupon Discount ({bookingData?.appliedCoupon || bookingData?.metadata?.appliedCoupon || 'COUPON'}):
                  </Typography>
                  <Typography variant="h6" color="success.main">
                    -{formatCurrency(bookingData?.metadata?.couponDiscount || bookingData?.couponDiscount || 0)}
                  </Typography>
                </Grid>
              )}

              {(bookingData?.serviceFee > 0 || bookingData?.metadata?.serviceFee > 0) && (
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Service fee:</Typography>
                  <Typography variant="h6">
                    {formatCurrency(bookingData?.serviceFee ?? bookingData?.metadata?.serviceFee ?? 0)}
                  </Typography>
                </Grid>
              )}

              {(bookingData?.tipAmount > 0 || bookingData?.metadata?.tipAmount > 0) && (
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Tip:</Typography>
                  <Typography variant="h6">
                    {formatCurrency(bookingData?.tipAmount ?? bookingData?.metadata?.tipAmount ?? 0)}
                  </Typography>
                </Grid>
              )}
              
              {/* Wallet Applied Line Item */}
              {(bookingData?.metadata?.appliedWalletBalance > 0 || bookingData?.walletBalanceUsed > 0) && (
                <Grid item xs={6}>
                  <Typography variant="body2" color="success.main">💳 Wallet Applied:</Typography>
                  <Typography variant="h6" color="success.main">
                    -{formatCurrency(bookingData?.metadata?.appliedWalletBalance || bookingData?.walletBalanceUsed || 0)}
                  </Typography>
                </Grid>
              )}

              {/* Gems Applied Line Item */}
              {(bookingData?.metadata?.appliedGems > 0 || bookingData?.gemsUsed > 0) && (
                <Grid item xs={6}>
                  <Typography variant="body2" color="success.main">
                    💎 {bookingData?.metadata?.appliedGems || bookingData?.gemsUsed || 0} gems used -{formatCurrency(((bookingData?.metadata?.appliedGems || bookingData?.gemsUsed || 0) / 1000) * 10)}
                  </Typography>
                </Grid>
              )}
              
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">Total Amount:</Typography>
                <Typography variant="h6" color="primary">{formatCurrency(bookingData.totalAmount || 0)}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">Payment Method:</Typography>
                <Typography variant="body1">{bookingData.paymentMethod || 'Credit Card'}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">Payment Confirmation:</Typography>
                <Typography variant="body1" sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}>
                  {bookingData.stripePaymentIntentId || bookingData.paymentConfirmationNumber || 'N/A'}
                </Typography>
              </Grid>
            </Grid>

            {bookingData.appliedWalletBalance > 0 && (
              <Box sx={{ mt: 2, p: 2, backgroundColor: '#f0fdf4', borderRadius: 1 }}>
                <Typography variant="body2" color="success.main">
                  Wallet Balance Applied: {formatCurrency(bookingData.appliedWalletBalance)}
                </Typography>
              </Box>
            )}

            {bookingData.appliedGems > 0 && (
              <Box sx={{ mt: 1, p: 2, backgroundColor: '#fef3c7', borderRadius: 1 }}>
                <Typography variant="body2" color="warning.main">
                  💎 {bookingData.appliedGems} gems used -{formatCurrency(((bookingData.appliedGems || 0) / 1000) * 10)}
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>

        {/* Special Instructions */}
        {bookingData.specialInstructions && (
          <Card sx={{ mb: 3, boxShadow: 3 }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 2 }}>
                Special Instructions
              </Typography>
              <Typography variant="body1">
                {bookingData.specialInstructions}
          </Typography>
            </CardContent>
          </Card>
        )}

          {/* Action Buttons */}
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: 4 }}>
            <Button
              variant="contained"
            size="large"
            onClick={handleBackToHome}
            sx={{ px: 4 }}
          >
            Back to Home
            </Button>
            <Button
              variant="outlined"
            size="large"
              onClick={() => navigate('/bookings')}
            sx={{ px: 4 }}
            >
            View My Bookings
            </Button>
          </Box>
      </Box>

      {/* Bottom Navigation - Same as Home Page */}
      <BottomNavigation />
    </Box>
  );
};

export default BookingSuccess; 